
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { Drop, Auction, Raffle } from '../domain/types';
import { clubRepository } from '../data/clubRepository';
import { clubService } from '../domain/services/clubService';
import { useToast } from './ToastContext';
import { useAuth } from './auth';

interface ClubContextType {
    drops: Drop[];
    auctions: Auction[];
    activeRaffle: Raffle | null;
    
    addDrop: (drop: Drop) => Promise<void>;
    updateDrop: (drop: Drop) => Promise<void>;
    deleteDrop: (id: string) => Promise<void>;
    
    addAuction: (auction: Auction) => Promise<void>;
    updateAuction: (auction: Auction) => Promise<void>;
    deleteAuction: (id: string) => Promise<void>;
    placeBid: (auctionId: string, amount: number, user: string) => Promise<boolean>;
    closeAuction: (auctionId: string) => Promise<void>;
    
    updateActiveRaffle: (raffle: Partial<Raffle>) => Promise<void>;
}

const ClubContext = createContext<ClubContextType | null>(null);

export const ClubProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { addToast } = useToast();
    const { user } = useAuth();

    const [drops, setDrops] = useState<Drop[]>([]);
    const [auctions, setAuctions] = useState<Auction[]>([]);
    const [activeRaffle, setActiveRaffle] = useState<Raffle | null>(null);

    const loadClubData = useCallback(async () => {
        const [fetchedDrops, fetchedAuctions, fetchedRaffle] = await Promise.all([
            clubRepository.getDrops(),
            clubRepository.getAuctions(),
            clubRepository.getActiveRaffle()
        ]);
        setDrops(fetchedDrops);
        setAuctions(fetchedAuctions);
        setActiveRaffle(fetchedRaffle);
    }, []);

    useEffect(() => {
        loadClubData();
    }, [loadClubData]);

    // --- DROPS ---
    const addDrop = useCallback(async (drop: Drop) => {
        await clubService.saveDrop(drop);
        loadClubData();
    }, [loadClubData]);

    const updateDrop = useCallback(async (updatedDrop: Drop) => {
        await clubService.saveDrop(updatedDrop);
        loadClubData();
    }, [loadClubData]);

    const deleteDrop = useCallback(async (id: string) => {
        await clubService.deleteDrop(id);
        loadClubData();
    }, [loadClubData]);

    // --- AUCTIONS ---
    const addAuction = useCallback(async (auction: Auction) => {
        await clubService.saveAuction(auction);
        loadClubData();
    }, [loadClubData]);

    const updateAuction = useCallback(async (updatedAuction: Auction) => {
        await clubService.saveAuction(updatedAuction);
        loadClubData();
    }, [loadClubData]);

    const deleteAuction = useCallback(async (id: string) => {
        await clubService.deleteAuction(id);
        loadClubData();
    }, [loadClubData]);

    const placeBid = useCallback(async (auctionId: string, amount: number, userName: string): Promise<boolean> => {
        if (!user) return false;
        const result = await clubService.placeBid(auctionId, amount, { id: user.id, name: userName });
        
        if (result.success) {
            // Optimistic update for UI speed
            setAuctions(prev => prev.map(a => {
                if (a.id === auctionId) {
                    return {
                        ...a,
                        currentBid: amount,
                        highestBidder: userName,
                        bids: [{ user: userName, amount, timestamp: new Date().toISOString() }, ...a.bids]
                    };
                }
                return a;
            }));
            // Background sync
            loadClubData();
            return true;
        } else {
            addToast(result.message || 'Error al pujar', 'error');
            return false;
        }
    }, [addToast, loadClubData]);

    const closeAuction = useCallback(async (auctionId: string) => {
        await clubService.closeAuction(auctionId);
        loadClubData();
    }, [loadClubData]);

    // --- RAFFLES ---
    const updateActiveRaffle = useCallback(async (updates: Partial<Raffle>) => {
        await clubService.updateRaffle(updates);
        loadClubData();
    }, [loadClubData]);

    const value = useMemo(() => ({
        drops, auctions, activeRaffle,
        addDrop, updateDrop, deleteDrop,
        addAuction, updateAuction, deleteAuction, placeBid, closeAuction,
        updateActiveRaffle
    }), [drops, auctions, activeRaffle, addDrop, updateDrop, deleteDrop, addAuction, updateAuction, deleteAuction, placeBid, closeAuction, updateActiveRaffle]);

    return (
        <ClubContext.Provider value={value}>
            {children}
        </ClubContext.Provider>
    );
};

export const useClub = () => {
    const context = useContext(ClubContext);
    if (!context) throw new Error("useClub must be used within ClubProvider");
    return context;
};
